/**
 * Contact Deduplication Utility
 *
 * Implements fuzzy matching algorithm for identifying duplicate contacts
 * using weighted scoring (Name 50%, Address 30%, Phone 20%).
 *
 * Features:
 * - Levenshtein + Jaro-Winkler similarity (fuzzball library)
 * - Field normalization (name, address, phone)
 * - Configurable threshold (70-100%)
 * - Weighted scoring algorithm
 * - Duplicate grouping
 */

import { ratio, token_sort_ratio, partial_ratio } from 'fuzzball';
import type { ContactItem } from '@shared/types/api';
import type {
  DeduplicationConfig,
  DeduplicationResult,
  DuplicateGroup,
  NormalizedContact,
  FieldComparison,
  DEFAULT_DEDUP_CONFIG,
} from '@features/contacts/types/deduplication';

/**
 * Normalize contact name for comparison
 * - Convert to lowercase
 * - Trim whitespace
 * - Remove common suffixes (CPA, MBA, PhD, etc.)
 * - Standardize punctuation
 */
export function normalizeName(name: string | null | undefined): string {
  if (!name) return '';

  let normalized = name.trim().toLowerCase();

  // Remove common professional suffixes
  const suffixes = [
    ', cpa',
    ', cma',
    ', phr',
    ', shrm-cp',
    ', mlis',
    ', crm',
    ', mba',
    ', pmp',
    ' cissp',
    ', phd',
    ', md',
    ', jr',
    ', sr',
    ', ii',
    ', iii',
    ', iv',
  ];

  for (const suffix of suffixes) {
    normalized = normalized.replace(suffix, '');
  }

  // Normalize punctuation
  normalized = normalized.replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

  return normalized;
}

/**
 * Normalize address for comparison
 * - Convert to lowercase
 * - Standardize abbreviations (St -> Street, Ave -> Avenue, etc.)
 * - Remove unit/suite numbers
 * - Normalize whitespace
 */
export function normalizeAddress(address: string | null | undefined): string {
  if (!address) return '';

  let normalized = address.trim().toLowerCase();

  // Standardize common abbreviations
  const abbreviations: Record<string, string> = {
    ' st\\b': ' street',
    ' ave\\b': ' avenue',
    ' blvd\\b': ' boulevard',
    ' dr\\b': ' drive',
    ' rd\\b': ' road',
    ' ln\\b': ' lane',
    ' ct\\b': ' court',
    ' pl\\b': ' place',
    ' pkwy\\b': ' parkway',
    ' hwy\\b': ' highway',
    ' n\\b': ' north',
    ' s\\b': ' south',
    ' e\\b': ' east',
    ' w\\b': ' west',
    ' ne\\b': ' northeast',
    ' nw\\b': ' northwest',
    ' se\\b': ' southeast',
    ' sw\\b': ' southwest',
  };

  for (const [abbr, full] of Object.entries(abbreviations)) {
    normalized = normalized.replace(new RegExp(abbr, 'gi'), full);
  }

  // Remove unit/suite/apt numbers (e.g., "Suite 200", "Apt 3B", "Unit 10")
  normalized = normalized.replace(/(suite|ste|apt|unit|#)\s*[a-z0-9-]+/gi, '');

  // Normalize whitespace and punctuation
  normalized = normalized.replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

  return normalized;
}

/**
 * Normalize phone number for comparison
 * - Extract digits only
 * - Remove country code if present
 * - Return last 10 digits (US phone number)
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';

  // Extract digits only
  const digitsOnly = phone.replace(/\D/g, '');

  // Return last 10 digits (ignore country code)
  if (digitsOnly.length >= 10) {
    return digitsOnly.slice(-10);
  }

  return digitsOnly;
}

/**
 * Normalize email for comparison
 * - Convert to lowercase
 * - Trim whitespace
 */
export function normalizeEmail(email: string | null | undefined): string {
  if (!email) return '';
  return email.trim().toLowerCase();
}

/**
 * Normalize contact for deduplication
 */
export function normalizeContact(contact: ContactItem): NormalizedContact {
  return {
    original: contact,
    normalizedName: normalizeName(contact.name),
    normalizedAddress: normalizeAddress(contact.address),
    normalizedPhone: normalizePhone(contact.phone),
    normalizedEmail: normalizeEmail(contact.email),
  };
}

/**
 * Calculate similarity score between two strings using fuzzball
 * - Uses token_sort_ratio for better word-order independence
 * - Returns score 0-100
 */
export function calculateSimilarity(str1: string, str2: string): number {
  if (!str1 && !str2) return 100; // Both empty = 100% match
  if (!str1 || !str2) return 0; // One empty = 0% match

  // Use token_sort_ratio for word-order independence
  // This handles "John Smith" vs "Smith, John"
  return token_sort_ratio(str1, str2);
}

/**
 * Calculate weighted match score between two contacts
 *
 * Scoring algorithm:
 * - Name similarity * name weight (default 50%)
 * - Address similarity * address weight (default 30%)
 * - Phone similarity * phone weight (default 20%)
 * - Email similarity * email weight (default 0%)
 *
 * Returns score 0-1 (0-100%)
 */
export function calculateMatchScore(
  contact1: NormalizedContact,
  contact2: NormalizedContact,
  config: DeduplicationConfig
): { score: number; reason: string } {
  let totalScore = 0;
  const reasons: string[] = [];

  // Name similarity
  if (config.matchFields.includes('name') && config.weightings.name > 0) {
    const nameSimilarity = calculateSimilarity(
      contact1.normalizedName,
      contact2.normalizedName
    ) / 100;
    totalScore += nameSimilarity * config.weightings.name;

    if (nameSimilarity > 0.8) {
      reasons.push(`Name match: ${Math.round(nameSimilarity * 100)}%`);
    }
  }

  // Address similarity
  if (config.matchFields.includes('address') && config.weightings.address > 0) {
    const addressSimilarity = calculateSimilarity(
      contact1.normalizedAddress,
      contact2.normalizedAddress
    ) / 100;
    totalScore += addressSimilarity * config.weightings.address;

    if (addressSimilarity > 0.8) {
      reasons.push(`Address match: ${Math.round(addressSimilarity * 100)}%`);
    }
  }

  // Phone similarity
  if (config.matchFields.includes('phone') && config.weightings.phone > 0) {
    const phoneSimilarity = calculateSimilarity(
      contact1.normalizedPhone,
      contact2.normalizedPhone
    ) / 100;
    totalScore += phoneSimilarity * config.weightings.phone;

    if (phoneSimilarity > 0.9) {
      reasons.push(`Phone match: ${Math.round(phoneSimilarity * 100)}%`);
    }
  }

  // Email similarity
  if (config.matchFields.includes('email') && config.weightings.email > 0) {
    const emailSimilarity = calculateSimilarity(
      contact1.normalizedEmail,
      contact2.normalizedEmail
    ) / 100;
    totalScore += emailSimilarity * config.weightings.email;

    if (emailSimilarity > 0.9) {
      reasons.push(`Email match: ${Math.round(emailSimilarity * 100)}%`);
    }
  }

  return {
    score: totalScore,
    reason: reasons.length > 0 ? reasons.join(', ') : 'Low similarity',
  };
}

/**
 * Find duplicate contacts using fuzzy matching
 *
 * Algorithm:
 * 1. Normalize all contacts
 * 2. Compare each contact pair
 * 3. Calculate weighted match score
 * 4. Group contacts with score >= threshold
 * 5. Sort groups by match score (highest first)
 *
 * @param contacts - Array of contacts to deduplicate
 * @param config - Deduplication configuration
 * @param ignoredPairs - Set of ignored contact ID pairs
 * @returns Deduplication result with duplicate groups
 */
export function findDuplicates(
  contacts: ContactItem[],
  config: DeduplicationConfig,
  ignoredPairs: Set<string> = new Set()
): DeduplicationResult {
  console.log('[Deduplicator] Starting deduplication:', {
    contacts: contacts.length,
    threshold: config.threshold,
    fields: config.matchFields,
  });

  // Step 1: Normalize all contacts
  const normalized = contacts.map(normalizeContact);

  // Step 2: Find duplicate pairs
  const duplicatePairs: Array<{
    contact1: ContactItem;
    contact2: ContactItem;
    score: number;
    reason: string;
  }> = [];

  for (let i = 0; i < normalized.length; i++) {
    for (let j = i + 1; j < normalized.length; j++) {
      const c1 = normalized[i];
      const c2 = normalized[j];

      // Check if pair is ignored
      const pairKey1 = `${c1.original.id}-${c2.original.id}`;
      const pairKey2 = `${c2.original.id}-${c1.original.id}`;
      if (ignoredPairs.has(pairKey1) || ignoredPairs.has(pairKey2)) {
        continue;
      }

      // Calculate match score
      const { score, reason } = calculateMatchScore(c1, c2, config);

      // If score >= threshold, add to duplicates
      if (score >= config.threshold) {
        duplicatePairs.push({
          contact1: c1.original,
          contact2: c2.original,
          score,
          reason,
        });
      }
    }
  }

  console.log('[Deduplicator] Found duplicate pairs:', duplicatePairs.length);

  // Step 3: Group duplicates (merge overlapping pairs)
  const groups = groupDuplicates(duplicatePairs);

  console.log('[Deduplicator] Grouped into', groups.length, 'groups');

  return {
    duplicatesFound: groups.length,
    groups: groups.sort((a, b) => b.matchScore - a.matchScore),
    totalScanned: contacts.length,
    config,
  };
}

/**
 * Group duplicate pairs into groups
 *
 * Handles transitive duplicates:
 * - If A matches B and B matches C, group A, B, C together
 *
 * @param pairs - Array of duplicate pairs
 * @returns Array of duplicate groups
 */
function groupDuplicates(
  pairs: Array<{
    contact1: ContactItem;
    contact2: ContactItem;
    score: number;
    reason: string;
  }>
): DuplicateGroup[] {
  if (pairs.length === 0) return [];

  // Build adjacency map
  const adjacencyMap = new Map<number, Set<number>>();
  const scoreMap = new Map<string, number>();
  const reasonMap = new Map<string, string>();

  for (const pair of pairs) {
    const id1 = pair.contact1.id;
    const id2 = pair.contact2.id;

    if (!adjacencyMap.has(id1)) adjacencyMap.set(id1, new Set());
    if (!adjacencyMap.has(id2)) adjacencyMap.set(id2, new Set());

    adjacencyMap.get(id1)!.add(id2);
    adjacencyMap.get(id2)!.add(id1);

    scoreMap.set(`${id1}-${id2}`, pair.score);
    scoreMap.set(`${id2}-${id1}`, pair.score);
    reasonMap.set(`${id1}-${id2}`, pair.reason);
    reasonMap.set(`${id2}-${id1}`, pair.reason);
  }

  // Find connected components (groups)
  const visited = new Set<number>();
  const groups: DuplicateGroup[] = [];

  for (const [contactId, neighbors] of adjacencyMap) {
    if (visited.has(contactId)) continue;

    // BFS to find all connected contacts
    const group = new Set<number>();
    const queue = [contactId];
    group.add(contactId);
    visited.add(contactId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentNeighbors = adjacencyMap.get(current) || new Set();

      for (const neighbor of currentNeighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          group.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    // Get contacts for group
    const groupContacts = pairs
      .flatMap((p) => [p.contact1, p.contact2])
      .filter((c) => group.has(c.id))
      .reduce((acc, c) => {
        if (!acc.find((x) => x.id === c.id)) acc.push(c);
        return acc;
      }, [] as ContactItem[]);

    // Calculate average match score for group
    const groupPairs = pairs.filter(
      (p) => group.has(p.contact1.id) && group.has(p.contact2.id)
    );
    const avgScore = groupPairs.reduce((sum, p) => sum + p.score, 0) / groupPairs.length;

    // Get primary reason (from highest scoring pair)
    const topPair = groupPairs.sort((a, b) => b.score - a.score)[0];

    groups.push({
      contacts: groupContacts,
      matchScore: avgScore,
      isPotentialMatch: true,
      matchReason: topPair?.reason || 'Multiple field matches',
    });
  }

  return groups;
}

/**
 * Compare two contacts field-by-field
 *
 * @param contact1 - First contact
 * @param contact2 - Second contact
 * @returns Array of field comparisons
 */
export function compareContacts(
  contact1: ContactItem,
  contact2: ContactItem
): FieldComparison[] {
  const fields: Array<{ field: keyof ContactItem; label: string }> = [
    { field: 'name', label: 'Name' },
    { field: 'first_name', label: 'First Name' },
    { field: 'last_name', label: 'Last Name' },
    { field: 'email', label: 'Email' },
    { field: 'phone', label: 'Phone' },
    { field: 'title', label: 'Title' },
    { field: 'address', label: 'Address' },
  ];

  return fields.map(({ field, label }) => {
    const val1 = contact1[field] as string | null | undefined;
    const val2 = contact2[field] as string | null | undefined;

    const str1 = val1 || '';
    const str2 = val2 || '';

    const isDifferent = str1.toLowerCase() !== str2.toLowerCase();
    const similarity = str1 && str2 ? calculateSimilarity(str1, str2) / 100 : 0;

    return {
      field: field as string,
      label,
      primaryValue: str1 || null,
      secondaryValue: str2 || null,
      isDifferent,
      similarity,
    };
  });
}
